import { Slot, SlotFillProvider, withFilters }                from '@wordpress/components';
import { Formik, Form }                                       from 'formik';
import React                                                  from 'react';
import { __, sprintf }                                       from '@wordpress/i18n';
import SettingsFormPropsInterface                             from '../../../interfaces/SettingsFormPropsInterface';
import SettingsFormPropsPlanInterface                         from '../../../interfaces/SettingsFormPropsPlanInterface';
import AdminPageSection                                       from '../AdminPageSection';
import CheckboxField                                          from '../Fields/CheckboxField';
import CheckboxGroupField                                     from '../Fields/CheckboxGroupField';
import CountriesMultiselectField                              from '../Fields/CountriesMultiselectField';
import RadioGroupField                                        from '../Fields/RadioGroupField';
import TextField                                              from '../Fields/TextField';
import MediumAlert                                            from '../MediumAlert';
import SevereAlert                                            from '../SevereAlert';
import SelectField                                            from '../Fields/SelectField';
import LockedFieldWrapper                                     from '../LockedFieldWrapper';

interface CheckoutFormSettingsInterface {
    disable_express_checkout: boolean;
    skip_cart_step: boolean;
    skip_shipping_step: boolean;
    enable_order_review_step: boolean;
    enable_one_page_checkout: boolean;
    registration_style: string;
    user_matching: string;
    disable_auto_open_login_modal: boolean;
    'wp_option/woocommerce_checkout_phone_field': string;
    enable_order_notes: boolean;
    enable_coupon_code_link: boolean;
    enable_discreet_address_1_fields: boolean;
    discreet_address_1_fields_order: string;
    enable_highlighted_countries: boolean;
    highlighted_countries: string[];
    enable_international_phone_field: boolean;
    international_phone_field_standard: string;
    force_different_billing_address: boolean;
    enabled_billing_address_fields: string[];
    hide_billing_address_for_free_orders: boolean;
    enable_address_autocomplete: boolean;
    enable_fetchify_address_autocomplete: boolean;
    fetchify_access_token: string;
    enable_mobile_cart_summary: boolean;
    enable_mobile_totals: boolean;
    show_mobile_coupon_field: boolean;
    show_logos_mobile: boolean;
    cart_summary_mobile_label: string;
    enable_order_pay: boolean;
    enable_smartystreets_integration: boolean;
    smartystreets_auth_id: string;
    smartystreets_auth_token: string;
    disable_domain_autocomplete: boolean;
    auto_select_free_shipping_method: boolean;
    enable_cart_editing: boolean;
    allow_checkout_cart_item_variation_changes: boolean;
    show_item_remove_button: boolean;
    cart_edit_empty_cart_redirect: string;
}

interface CheckoutFormWooCommerceSettingsInterface {
    countries: any;
}

interface CheckoutFormConditionalSettingsInterface {
    order_notes_enable: boolean;
}

interface CheckoutSettingsFormPropsInterface extends SettingsFormPropsInterface {
    settings: CheckoutFormSettingsInterface;
    woocommerce_settings: CheckoutFormWooCommerceSettingsInterface;
    conditional_settings: CheckoutFormConditionalSettingsInterface;
    plan: SettingsFormPropsPlanInterface;
}

const CheckoutSettingsForm: React.FC<CheckoutSettingsFormPropsInterface> = ( props ) => {
    const { saveSettings, isLoading, searchTerm } = props;

    const AdditionalSettings = withFilters(
        'CheckoutWC.Admin.Settings.Pages.Checkout',
    )( () => <></> );

    return (
        <SlotFillProvider>
            <AdditionalSettings />
            <Formik
                initialValues={{
                    ...props.settings,
                    enabled_billing_address_fields: props.settings.enabled_billing_address_fields
                        ? props.settings.enabled_billing_address_fields
                        : [ 'billing_first_name',
                            'billing_last_name',
                            'billing_address_1',
                            'billing_address_2',
                            'billing_company',
                            'billing_country',
                            'billing_postcode',
                            'billing_state',
                            'billing_city',
                            'billing_phone',
                        ],
                }}
                enableReinitialize={true}
                onSubmit={async ( values ) => {
                    await saveSettings( values );
                }}
            >
                {( { values } ) => (
                    <Form className={'space-y-6 transition-all'} style={{ filter: isLoading ? 'blur(2px)' : 'none' }}>
                        <AdminPageSection
                            title={__( 'Steps', 'checkout-wc' )}
                            description={__( 'Control the checkout steps.', 'checkout-wc' )}
                            content={
                                <>
                                    <CheckboxField
                                        name="skip_cart_step"
                                        label={__( 'Disable Cart Step', 'checkout-wc' )}
                                        description={__( 'Disable to skip the cart and redirect customers directly to checkout after adding a product to the cart. (Incompatible with Side Cart)', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />
                                    <CheckboxField
                                        name="skip_shipping_step"
                                        label={__( 'Disable Shipping Step', 'checkout-wc' )}
                                        description={__( 'Disable to hide the shipping method step. Useful if you only have one shipping option for all orders.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    <LockedFieldWrapper plan={props.plan} slug={'order-review-step-one-page-checkout'} locked={props.plan.plan_level < 1} requiredPlans={props.plan.labels.required_list[ 1 ]}>
                                        <CheckboxField
                                            name="enable_order_review_step"
                                            label={__( 'Enable Order Review Step', 'checkout-wc' )}
                                            description={__( 'Adds a review step after payment information before finalizing order. Useful for jurisdictions which require additional confirmation before order submission. (Cannot be used with One Page Checkout)', 'checkout-wc' )}
                                            searchTerm={searchTerm}
                                            disabled={props.plan.plan_level < 1}
                                        />

                                        <CheckboxField
                                            name="enable_one_page_checkout"
                                            label={__( 'Enable One Page Checkout', 'checkout-wc' )}
                                            description={__( 'Show all checkout steps on  one page. Useful for digital stores. (Cannot be used with Order Review Step)', 'checkout-wc' )}
                                            searchTerm={searchTerm}
                                            disabled={props.plan.plan_level < 1}
                                        />
                                    </LockedFieldWrapper>

                                    <Slot name="CheckoutWC.Admin.Pages.Checkout.Steps" />
                                </>
                            }
                        />
                        <AdminPageSection
                            title={__( 'Shipping Options', 'checkout-wc' )}
                            description={__( 'Control shipping options.', 'checkout-wc' )}
                            content={
                                <>
                                    <LockedFieldWrapper plan={props.plan} slug={'auto-select-free-shipping'} locked={props.plan.plan_level < 1} requiredPlans={props.plan.labels.required_list[ 1 ]}>
                                        <CheckboxField
                                            name="auto_select_free_shipping_method"
                                            label={__( 'Auto Select Free Shipping Method (if available)', 'checkout-wc' )}
                                            description={__( 'WooCommerce has a habit of not selecting the free shipping method if it is setup to be conditionally enabled. This option auto-selects the free shipping on page load.', 'checkout-wc' )}
                                            searchTerm={searchTerm}
                                            disabled={props.plan.plan_level < 1}
                                        />
                                    </LockedFieldWrapper>

                                    <Slot name="CheckoutWC.Admin.Pages.Checkout.ShippingOptions" />
                                </>
                            }
                        />
                        <AdminPageSection
                            title={__( 'Login and Registration', 'checkout-wc' )}
                            description={__( 'Control how login and registration function on your checkout page.', 'checkout-wc' )}
                            content={
                                <>
                                    <CheckboxField
                                        name="disable_auto_open_login_modal"
                                        label={__( 'Disable Automatic Login Modal', 'checkout-wc' )}
                                        description={__( 'Normally the login modal automatically opens if the entered email address matches an existing account. This disables that behavior.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    <RadioGroupField
                                        name='registration_style'
                                        label={__( 'Registration', 'checkout-wc' )}
                                        description={__( 'Choose how customers obtain a password when registering an account.', 'checkout-wc' )}
                                        options={[
                                            {
                                                value: 'enhanced',
                                                label: __( 'Enhanced (Recommended)', 'checkout-wc' ),
                                                description: __( 'Automatically generate a username and password and email it to the customer using the native WooCommerce functionality. (Recommended)', 'checkout-wc' ),
                                            },
                                            {
                                                value: 'woocommerce',
                                                label: __( 'WooCommerce Default', 'checkout-wc' ),
                                                description: __( 'A password field is provided for the customer to select their own password. Not recommended.', 'checkout-wc' ),
                                            },
                                        ]}
                                        searchTerm={searchTerm}
                                    />

                                    <LockedFieldWrapper plan={props.plan} slug={'user-matching'} locked={props.plan.plan_level < 1} requiredPlans={props.plan.labels.required_list[ 1 ]}>
                                        <RadioGroupField
                                            name='user_matching'
                                            label={__( 'User Matching', 'checkout-wc' )}
                                            description={__( 'Choose how to handle guest orders and accounts.', 'checkout-wc' )}
                                            disabled={props.plan.plan_level < 1}
                                            options={[
                                                {
                                                    value: 'enabled',
                                                    label: __( 'Enabled (Recommended)', 'checkout-wc' ),
                                                    description: __( 'Automatically matches guest orders to user accounts on new purchase as well as on registration of a new user. (Recommended)', 'checkout-wc' ),
                                                },
                                                {
                                                    value: 'woocommerce',
                                                    label: __( 'WooCommerce Default', 'checkout-wc' ),
                                                    description: __( 'Guest orders will not be linked to matching accounts.', 'checkout-wc' ),
                                                },
                                            ]}
                                            searchTerm={searchTerm}
                                        />
                                    </LockedFieldWrapper>

                                    <Slot name="CheckoutWC.Admin.Pages.Checkout.LoginRegistration" />
                                </>
                            }
                        />

                        <AdminPageSection
                            title={__( 'Field Options', 'checkout-wc' )}
                            description={__( 'Control how different checkout fields appear.', 'checkout-wc' )}
                            content={
                                <>
                                    <SelectField
                                        name={'wp_option/woocommerce_checkout_phone_field'}
                                        label={__( 'Phone Field', 'checkout-wc' )}
                                        description={__( 'Determines whether the WooCommerce native phone field is enabled.', 'checkout-wc' )}
                                        options={[
                                            {
                                                value: 'hidden',
                                                label: __( 'Hidden', 'checkout-wc' ),
                                            },
                                            {
                                                value: 'optional',
                                                label: __( 'Optional', 'checkout-wc' ),
                                            },
                                            {
                                                value: 'required',
                                                label: __( 'Required', 'checkout-wc' ),
                                            },
                                        ]}
                                        searchTerm={searchTerm}
                                    />

                                    <CheckboxField
                                        name="enable_order_notes"
                                        label={__( 'Enable Order Notes Field', 'checkout-wc' )}
                                        description={__( 'Enable or disable WooCommerce Order Notes field. (Default: Disabled)', 'checkout-wc' )}
                                        enabled={props.conditional_settings.order_notes_enable}
                                        searchTerm={searchTerm}
                                    />
                                    {!props.conditional_settings.order_notes_enable
                                        && (
                                            <MediumAlert description={__( 'Order Notes field is being controlled by a filter or a plugin.', 'checkout-wc' )} />
                                        )
                                    }

                                    <CheckboxField
                                        name="enable_coupon_code_link"
                                        label={__( 'Hide Coupon Code Field Behind Link', 'checkout-wc' )}
                                        description={__( 'Initially hide coupon field until "Have a coupon code?" link is clicked.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    <CheckboxField
                                        name="hide_optional_address_fields_behind_link"
                                        label={__( 'Hide Optional Address Fields Behind Links', 'checkout-wc' )}
                                        description={__( 'Recommended to increase conversions. Example link text: Add Company (optional)', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    <CheckboxField
                                        name="disable_domain_autocomplete"
                                        label={__( 'Disable Email Domain Autocomplete', 'checkout-wc' )}
                                        description={__( 'Disable email domain autocomplete.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    <LockedFieldWrapper plan={props.plan} slug={'field-options'} locked={props.plan.plan_level < 1} requiredPlans={props.plan.labels.required_list[ 1 ]}>
                                        <CheckboxField
                                            name="enable_discreet_address_1_fields"
                                            label={__( 'Enable Separate House Number and Street Name Address Fields', 'checkout-wc' )}
                                            description={__( 'Values are combined into a single address_1 field based on country selected by customer.', 'checkout-wc' )}
                                            searchTerm={searchTerm}
                                            disabled={props.plan.plan_level < 1}
                                        />

                                        {props.plan.plan_level >= 1 && values.enable_discreet_address_1_fields
                                        && (
                                            <RadioGroupField
                                                name='discreet_address_1_fields_order'
                                                label={__( 'Separate Address Fields Display Order', 'checkout-wc' )}
                                                description={__( 'Choose how display separate address 1 fields.', 'checkout-wc' )}
                                                nested={true}
                                                options={[
                                                    {
                                                        value: 'default',
                                                        label: __( '[House Number] [Street Name]', 'checkout-wc' ),
                                                        description: __( 'Display the House Number before the Street Name. (Default)', 'checkout-wc' ),
                                                    },
                                                    {
                                                        value: 'alternate',
                                                        label: __( '[Street Name] [House Number]', 'checkout-wc' ),
                                                        description: __( 'Display the Street Name before the House Number.', 'checkout-wc' ),
                                                    },
                                                ]}
                                                searchTerm={searchTerm}
                                            />
                                        )
                                        }

                                        {values.enable_discreet_address_1_fields && values.enable_address_autocomplete && (
                                            <SevereAlert
                                                description={__( 'Separate Address Fields is incompatible with Google Address Autocomplete. <a target="_blank" class="text-blue-600 underline" href="https://www.checkoutwc.com/documentation/google-address-autocomplete-and-discreet-house-number-and-street-name-address-fields/">Learn More</a>', 'checkout-wc' )}
                                            />
                                        )}

                                        <CheckboxField
                                            name="use_fullname_field"
                                            label={__( 'Enable Full Name Field', 'checkout-wc' )}
                                            description={__( 'Enable to replace first and last name fields with a single full name field.', 'checkout-wc' )}
                                            searchTerm={searchTerm}
                                            disabled={props.plan.plan_level < 1}
                                        />

                                        <CheckboxField
                                            name="enable_highlighted_countries"
                                            label={__( 'Enable Highlighted Countries', 'checkout-wc' )}
                                            description={__( 'Promote selected countries to the top of the countries list in country dropdowns.', 'checkout-wc' )}
                                            searchTerm={searchTerm}
                                            disabled={props.plan.plan_level < 1}
                                        />

                                        {props.plan.plan_level >= 1 && values.enable_highlighted_countries
                                        && (
                                            <CountriesMultiselectField
                                                name='highlighted_countries'
                                                label={__( 'Highlighted Countries', 'checkout-wc' )}
                                                description={__( 'The countries to show first in country dropdowns.', 'checkout-wc' )}
                                                nested={true}
                                                hide={!values.enable_highlighted_countries}
                                                countries={props.woocommerce_settings.countries}
                                                searchTerm={searchTerm}
                                            />
                                        )
                                        }

                                        <CheckboxField
                                            name="enable_international_phone_field"
                                            label={__( 'Enable International Phone Field', 'checkout-wc' )}
                                            description={__( 'Validate phone number entry based on selected country. Replaces phone field placeholder with example phone number. Stores phone number according to International Phone Format.', 'checkout-wc' )}
                                            searchTerm={searchTerm}
                                            disabled={props.plan.plan_level < 1}
                                        />

                                        {props.plan.plan_level >= 1 && values.enable_international_phone_field
                                        && (
                                            <RadioGroupField
                                                name='international_phone_field_standard'
                                                label={__( 'International Phone Format', 'checkout-wc' )}
                                                description={__( 'Choose how to store phone numbers.', 'checkout-wc' )}
                                                nested={true}
                                                options={[
                                                    {
                                                        value: 'raw',
                                                        label: __( 'Raw Value (No Formatting)', 'checkout-wc' ),
                                                        description: __( 'The number is stored exactly how the user entered it.', 'checkout-wc' ),
                                                    },
                                                    {
                                                        value: 'E164',
                                                        label: __( 'E164', 'checkout-wc' ),
                                                        description: __( 'Format phone number with E164 standard.', 'checkout-wc' ),
                                                    },
                                                    {
                                                        value: 'INTERNATIONAL',
                                                        label: __( 'International', 'checkout-wc' ),
                                                        description: __( 'Format phone number with RFC3966 standard without the tel: prefix', 'checkout-wc' ),
                                                    },
                                                    {
                                                        value: 'NATIONAL',
                                                        label: __( 'National', 'checkout-wc' ),
                                                        description: __( 'Format phone number based on selected country. US Example (555) 555 - 5555, UK Example: 07911 123457', 'checkout-wc' ),
                                                    },
                                                    {
                                                        value: 'RFC3966',
                                                        label: __( 'RFC3966', 'checkout-wc' ),
                                                        description: __( 'Format phone number with RFC3966 standard.', 'checkout-wc' ),
                                                    },
                                                ]}
                                                searchTerm={searchTerm}
                                            />
                                        )
                                        }
                                    </LockedFieldWrapper>
                                    <Slot name="CheckoutWC.Admin.Pages.Checkout.FieldOptions" />
                                </>
                            }
                        />
                        <AdminPageSection
                            title={__( 'Address Options', 'checkout-wc' )}
                            description={__( 'Control address fields.', 'checkout-wc' )}
                            content={
                                <>
                                    <CheckboxField
                                        name="force_different_billing_address"
                                        label={__( 'Force Different Billing Address', 'checkout-wc' )}
                                        description={__( 'Remove option to use shipping address as billing address.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    <CheckboxField
                                        name="hide_billing_address_for_free_orders"
                                        label={__( 'Hide Billing Address For Free Orders', 'checkout-wc' )}
                                        description={__( 'Remove the billing address fields from checkout for free orders.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    <CheckboxGroupField
                                        name='enabled_billing_address_fields'
                                        label={__( 'Enabled Billing Address Fields', 'checkout-wc' )}
                                        description={__( 'Choose which billing address fields are visible for customers.', 'checkout-wc' )}
                                        options={[
                                            {
                                                value: 'billing_first_name',
                                                label: __( 'First Name', 'checkout-wc' ),
                                            },
                                            {
                                                value: 'billing_last_name',
                                                label: __( 'Last Name', 'checkout-wc' ),
                                            },
                                            {
                                                value: 'billing_address_1',
                                                label: __( 'Address 1', 'checkout-wc' ),
                                            },
                                            {
                                                value: 'billing_address_2',
                                                label: __( 'Address 2', 'checkout-wc' ),
                                            },
                                            {
                                                value: 'billing_company',
                                                label: __( 'Company', 'checkout-wc' ),
                                            },
                                            {
                                                value: 'billing_country',
                                                label: __( 'Country', 'checkout-wc' ),
                                            },
                                            {
                                                value: 'billing_postcode',
                                                label: __( 'Postcode', 'checkout-wc' ),
                                            },
                                            {
                                                value: 'billing_state',
                                                label: __( 'State', 'checkout-wc' ),
                                            },
                                            {
                                                value: 'billing_city',
                                                label: __( 'City', 'checkout-wc' ),
                                            },
                                            {
                                                value: 'billing_phone',
                                                label: __( 'Phone', 'checkout-wc' ),
                                            },
                                        ]}
                                        searchTerm={searchTerm}
                                    />
                                    {
                                        ( !values.enabled_billing_address_fields.includes( 'billing_country' ) || !values.enabled_billing_address_fields.includes( 'billing_state' ) )
                                        && (
                                            <MediumAlert
                                                description={__( 'Disabling the country or state field causes issues with some gateways. Consider leaving these fields enabled.', 'checkout-wc' )}
                                            />
                                        )
                                    }

                                    <Slot name="CheckoutWC.Admin.Pages.Checkout.AddressOptions" />
                                </>
                            }
                        />
                        <AdminPageSection
                            title={__( 'Address Completion and Validation', 'checkout-wc' )}
                            description={__( 'Control some mobile only checkout behaviors.', 'checkout-wc' )}
                            content={
                                <>
                                    <LockedFieldWrapper plan={props.plan} slug={'address-complete-and-validation'} locked={props.plan.plan_level < 1} requiredPlans={props.plan.labels.required_list[ 1 ]}>
                                        <CheckboxField
                                            name="enable_address_autocomplete"
                                            label={__( 'Enable Google Address Autocomplete', 'checkout-wc' )}
                                            description={sprintf( 'Enable or disable Google Address Autocomplete feature. <a href="%s" class="text-blue-600 underline">%s</a>', ( window as any )?.cfw_google_address_autocomplete?.google_api_key_settings_page_url ?? '', 'Requires Google API key.' )}
                                            searchTerm={searchTerm}
                                            disabled={props.plan.plan_level < 1}
                                        />

                                        {props.plan.plan_level >= 1 && values.enable_discreet_address_1_fields && values.enable_address_autocomplete && (
                                            <SevereAlert
                                                description={__( 'Google Address Autocomplete cannot be used with Separate Address Fields. <a target="_blank" class="text-blue-600 underline" href="https://www.checkoutwc.com/documentation/google-address-autocomplete-and-discreet-house-number-and-street-name-address-fields/">Learn More</a>', 'checkout-wc' )}
                                            />
                                        )}

                                        <CheckboxField
                                            name="enable_fetchify_address_autocomplete"
                                            label={__( 'Enable Fetchify Address Autocomplete', 'checkout-wc' )}
                                            description={__( 'Enable or disable Fetchify address autocomplete feature. <a href="https://fetchify.com" class="text-blue-600 underline" target="_blank">Requires Fetchify access token.</a>', 'checkout-wc' )}
                                            searchTerm={searchTerm}
                                            disabled={props.plan.plan_level < 1}
                                        />

                                        {props.plan.plan_level >= 1 && values.enable_fetchify_address_autocomplete
                                        && (
                                            <TextField
                                                name='fetchify_access_token'
                                                label={__( 'Fetchify Access Token', 'checkout-wc' )}
                                                description={__( 'Your Fetchify access token.', 'checkout-wc' )}
                                                nested={true}
                                                searchTerm={searchTerm}
                                            />
                                        )
                                        }

                                        {
                                            values.enable_fetchify_address_autocomplete && values.enable_address_autocomplete
                                        && (
                                            <SevereAlert
                                                description={__( 'You can only use one address autocomplete service at a time. Please disable one of the services.', 'checkout-wc' )}
                                            />
                                        )
                                        }

                                        <CheckboxField
                                            name="enable_smartystreets_integration"
                                            label={__( 'Enable Smarty Address Validation', 'checkout-wc' )}
                                            description={__( 'Validates shipping address with Smarty.com and provides alternative, corrected addresses for incorrect or incomplete addresses.', 'checkout-wc' )}
                                            searchTerm={searchTerm}
                                            disabled={props.plan.plan_level < 1}
                                        />

                                        {props.plan.plan_level >= 1 && values.enable_smartystreets_integration
                                        && (
                                            <>
                                                <TextField
                                                    name='smartystreets_auth_id'
                                                    label={__( 'Smarty Auth ID', 'checkout-wc' )}
                                                    description={__( 'Smarty Auth ID. Available in your <a target="_blank" href="https://www.smarty.com/account/keys" class="text-blue-600 underline">Smarty Account</a>', 'checkout-wc' )}
                                                    searchTerm={searchTerm}
                                                    nested={true}
                                                />
                                                <TextField
                                                    name='smartystreets_auth_token'
                                                    label={__( 'Smarty Auth Token', 'checkout-wc' )}
                                                    description={__( 'Smarty Auth Token. Available in your <a target="_blank" href="https://www.smarty.com/account/keys" class="text-blue-600 underline">Smarty Account</a>.', 'checkout-wc' )}
                                                    searchTerm={searchTerm}
                                                    nested={true}
                                                />
                                            </>
                                        )
                                        }
                                    </LockedFieldWrapper>

                                    <Slot name="CheckoutWC.Admin.Pages.Checkout.AddressCompletionValidation" />
                                </>
                            }
                        />
                        <AdminPageSection
                            title={__( 'Cart Summary', 'checkout-wc' )}
                            description={__( 'Configure the Cart Summary on the checkout page.', 'checkout-wc' )}
                            content={
                                <>
                                    <LockedFieldWrapper plan={props.plan} slug={'cart-editing-at-checkout'} locked={props.plan.plan_level < 1} requiredPlans={props.plan.labels.required_list[ 1 ]}>
                                        <CheckboxField
                                            name="enable_cart_editing"
                                            label={__( 'Enable Cart Editing At Checkout', 'checkout-wc' )}
                                            description={__( 'Enable or disable Cart Editing. Allows customer to remove or adjust quantity of cart items at checkout.', 'checkout-wc' )}
                                            searchTerm={searchTerm}
                                            disabled={props.plan.plan_level < 1}
                                        />

                                        {props.plan.plan_level >= 1 && values.enable_cart_editing
                                            && (
                                                <>
                                                    <CheckboxField
                                                        name="allow_checkout_cart_item_variation_changes"
                                                        label={__( 'Allow Variation Changes', 'checkout-wc' )}
                                                        description={__( 'Displays an edit link under cart items that allows customers to change which variation is selected in the cart.', 'checkout-wc' )}
                                                        nested={true}
                                                        searchTerm={searchTerm}
                                                    />
                                                    <CheckboxField
                                                        name="show_item_remove_button"
                                                        label={__( 'Show Item Remove Button', 'checkout-wc' )}
                                                        description={__( 'When hovering over an item, show a button (X) to remove the item.', 'checkout-wc' )}
                                                        nested={true}
                                                        searchTerm={searchTerm}
                                                    />
                                                    <TextField
                                                        name="cart_edit_empty_cart_redirect"
                                                        label={__( 'Cart Editing Empty Cart Redirect', 'checkout-wc' )}
                                                        description={__( 'URL to redirect to when customer empties cart from checkout page. If left blank, customer will be redirected to the cart page.', 'checkout-wc' )}
                                                        nested={true}
                                                        searchTerm={searchTerm}
                                                    />
                                                </>
                                            )
                                        }
                                    </LockedFieldWrapper>

                                    <CheckboxField
                                        name="enable_sticky_cart_summary"
                                        label={__( 'Enable Sticky Cart Summary', 'checkout-wc' )}
                                        description={__( 'If cart summary is shorter than the rest of the content, it will stick to the top of the screen when scrolling.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />
                                </>
                            }
                        />
                        <AdminPageSection
                            title={__( 'Mobile Options', 'checkout-wc' )}
                            description={__( 'Control mobile specific features.', 'checkout-wc' )}
                            content={
                                <>
                                    <CheckboxField
                                        name="enable_mobile_cart_summary"
                                        label={__( 'Enable Mobile Cart Summary', 'checkout-wc' )}
                                        description={__( 'Shows the cart, promo field, and totals at the bottom of the first checkout step.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    <CheckboxField
                                        name="enable_mobile_totals"
                                        label={__( 'Enable Mobile Totals', 'checkout-wc' )}
                                        description={__( 'Shows cart totals right above the place order button on mobile.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    <CheckboxField
                                        name="show_mobile_coupon_field"
                                        label={__( 'Enable Mobile Coupon Field', 'checkout-wc' )}
                                        description={__( 'Show coupon field above payment gateways on mobile devices. Helps customers find the coupon field without expanding the cart summary.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />
                                    {
                                        values.enable_mobile_cart_summary && values.show_mobile_coupon_field
                                        && (
                                            <SevereAlert
                                                description={__( 'Enable Mobile Cart Summary is not compatible with Enable Mobile Coupon Field. Please disable one of the options.', 'checkout-wc' )}
                                            />
                                        )
                                    }
                                    <CheckboxField
                                        name="show_logos_mobile"
                                        label={__( 'Enable Mobile Credit Card Logos', 'checkout-wc' )}
                                        description={__( 'Show the credit card logos on mobile. Note: Many gateway logos cannot be rendered properly on mobile. It is recommended you test before enabling. Default: Off', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />
                                    <TextField
                                        name='cart_summary_mobile_label'
                                        label={__( 'Cart Summary Mobile Label', 'checkout-wc' )}
                                        description={__( 'Example: Show order summary and coupons. If left blank, this default will be used: Show order summary', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />
                                    <Slot name="CheckoutWC.Admin.Pages.Checkout.MobileOptions" />
                                </>
                            }
                        />
                        <AdminPageSection
                            title={__( 'Order Pay', 'checkout-wc' )}
                            description={__( 'Enable CheckoutWC template for the Order Pay / Customer Payment Page endpoint.', 'checkout-wc' )}
                            content={
                                <>
                                    <LockedFieldWrapper plan={props.plan} slug={'order-pay'} locked={props.plan.plan_level < 1} requiredPlans={props.plan.labels.required_list[ 1 ]}>
                                        <CheckboxField
                                            name="enable_order_pay"
                                            label={__( 'Enable Order Pay Page', 'checkout-wc' )}
                                            description={__( 'Use CheckoutWC templates for Order Pay page.', 'checkout-wc' )}
                                            searchTerm={searchTerm}
                                            disabled={props.plan.plan_level < 1}
                                        />
                                    </LockedFieldWrapper>

                                    <Slot name="CheckoutWC.Admin.Pages.Checkout.OrderPay" />
                                </>
                            }
                        />
                        <button className={'cfw_admin_page_submit hidden'} type="submit">Submit</button>
                    </Form>
                )}
            </Formik>
        </SlotFillProvider>
    );
};

export default CheckoutSettingsForm;
